/**
 * Where a deal is, and what may be done to it there.
 *
 * A leaf module: NO imports, so the UI, the store and the tests can all use it
 * without dragging in supabase-js or React Query. Same shape as
 * `src/lib/messaging/conversation-gate.ts` and `src/lib/dd/dd-entry.ts`.
 *
 * WHY THIS EXISTS
 * ---------------
 * Five models each claimed to say where a deal was — `dealflow.status`,
 * `data_room_access_requests.status`, `dd_reports.status`/`.decision`,
 * `commitments.status`, and the existence of a `portfolio_companies` row — and
 * none wrote to another. Concluding a due diligence as 'invest' left the card
 * exactly where it was and never reached the portfolio.
 *
 * Meanwhile every screen hardcoded its own idea of the legal moves:
 * `DealflowCard` rendered a fixed Pass/Interested pair regardless of status,
 * `dealflow.tsx` dropped any status that was not interested/passed, and
 * `duediligence/[id].tsx` invented a third predicate. That is why a card kept
 * offering "Interested" to a deal already marked interested.
 *
 * The stage is now the single source of truth, and buttons, placement and
 * dashboard counts are DERIVED from it. A screen must never test a stage
 * literal; the CI guard ARCH-13 fails the build if one does. (This comment said
 * ARCH-07 before the guard was written — ARCH-07 is the raw-`<Modal>` guard, and
 * the code was picked here in advance of the guard existing.)
 */

/**
 * The spine. One value per real position in an investor's process.
 *
 * `awaiting_access` exists because diligence is not something an investor can
 * start on their own. They REQUEST the data room; the founder's authorisation
 * is the single event that both opens the documents and advances the deal. The
 * gap between the ask and the answer is a real, observable position — a deal
 * sitting there is neither merely `interested` nor yet in `diligence` — and
 * before it existed the client pretended the request WAS entry, which promised
 * documents it could not deliver.
 */
export type DealStage =
  | 'screening'       // in the pipeline, not yet triaged (absorbs new + reviewing)
  | 'interested'      // passed screening; the next move is to ask for the data room
  | 'awaiting_access' // access requested, founder has not answered. Documents shut.
  | 'diligence'       // DD underway — entered ONLY by the founder's approval
  | 'invested'        // terminal
  | 'rejected'        // terminal (absorbs passed)
  | 'monitoring';     // terminal, but re-engageable when they raise again

export const DEAL_STAGES: readonly DealStage[] = [
  'screening', 'interested', 'awaiting_access', 'diligence',
  'invested', 'rejected', 'monitoring',
] as const;

/** What a surface may offer. `view` and `open-diligence` navigate; the rest transition. */
export type DealAction =
  | 'mark-interested'
  | 'request-access'
  | 'reject'
  | 'monitor'
  | 're-engage'
  | 'open-diligence'
  | 'view';

/** Where a card at this stage is listed. A stage may appear on more than one. */
export type Surface = 'dealflow' | 'diligence' | 'portfolio' | 'watchlist' | 'archive';

/**
 * `rejected` and `monitoring` are reachable from every live stage: a round can
 * close, or an investor can decide to follow, at any point — including before
 * diligence ever starts.
 */
const LIVE_ENDINGS: readonly DealStage[] = ['rejected', 'monitoring'] as const;

const TERMINAL: readonly DealStage[] = ['invested', 'rejected', 'monitoring'] as const;

/**
 * Which moves are LEGAL. Not the same table as ACTIONS below, which says which
 * moves a CLIENT may offer, and the difference is load-bearing for exactly one
 * entry — see the note on `awaiting_access`.
 */
const TRANSITIONS: Readonly<Record<DealStage, readonly DealStage[]>> = {
  // `awaiting_access` is reachable straight from `screening` (owner's decision,
  // 2026-08-06). Asking for the data room IS an expression of interest — the
  // strongest one an investor makes before diligence — so requiring a
  // `mark-interested` click first was ceremony, not triage. It also closes a
  // real contradiction: `screening` is the DEFAULT stage (what
  // `investor-analyze-deck` and the inbox shortlist write), so most deals sat
  // there while the screen offered a request the spine called illegal. The
  // exception that papered over it (`useMoveToAwaitingAccess`, an ungated stage
  // write) is deleted, not narrowed — one gated path for every stage write.
  screening:  ['interested', 'awaiting_access', ...LIVE_ENDINGS],
  interested: ['awaiting_access', ...LIVE_ENDINGS],
  // `diligence` IS listed here and NO action targets it. That is deliberate, not
  // an oversight to tidy away.
  //
  // This table says which moves are LEGAL; ACTIONS says which the CLIENT may
  // offer. `awaiting_access → diligence` is legal, and the only thing that
  // SHOULD perform it is the founder's approval, server-side, in
  // respond_to_data_room_request (20260805140001).
  //
  // "Should", not "can": measured on staging, the `Investors can manage their
  // dealflow` policy is FOR ALL with no WITH CHECK, so an investor can forge
  // this column on their own row via the REST API. Keeping them out is a client
  // convention, not an enforced boundary. It costs them nothing — the enforced
  // control is the GRANT (public.data_room_access), which only that RPC writes
  // and only a founder can call. Do not read this table as a security boundary.
  //
  // That RPC guards on the SOURCE stage — it writes only when
  // the deal is at `awaiting_access` — which is this entry, transcribed into
  // SQL by hand rather than called into. So the two must be kept in step: delete
  // the entry here and `canTransition` starts denying a move the database
  // performs every time a founder says yes. Add an action for it and the
  // investor gets a button that opens their own data-room access. Both tables
  // have to disagree on this one row for the product to be right.
  //
  // `interested` is the way back: a declined request returns the deal to where
  // it was, rather than inventing a `declined` stage nothing can leave.
  //
  // A request that STARTED at `screening` therefore lands at `interested` when
  // the founder declines, not back at `screening`. That is right, not a bug the
  // widening above introduced: by asking, the investor expressed interest, and
  // a no from the founder should not erase it. The alternative — remembering
  // where the request came from so a decline could return it there — would mean
  // an origin column, a second store of a fact the stage already carries.
  // (`respond_to_data_room_request`, 20260805140001, writes the `interested`.)
  awaiting_access: ['diligence', 'interested', ...LIVE_ENDINGS],
  diligence:  ['invested', ...LIVE_ENDINGS],
  invested:   [],
  rejected:   [],
  // Terminal in that nothing advances out of it automatically. The single way
  // back is an explicit, investor-initiated re-engage when the company opens
  // its next round — without it, "interested to follow" is a dead end.
  monitoring: ['interested'],
};

const ACTIONS: Readonly<Record<DealStage, readonly DealAction[]>> = {
  // `request-access` leads, as it does at `interested` — it is the forward move,
  // and the rest is triage. Offering it HERE is the load-bearing half of the
  // decision above: `startup-detail.tsx` derives
  // `requestOfferedInActionBar = dealActions.includes('request-access')`, and
  // the Data Room card's own hand-rolled CTA renders only when that is false.
  // So this line is what collapses two identically-labelled buttons into one
  // derived path on the stage most deals actually sit at.
  screening:  ['request-access', 'reject', 'mark-interested', 'monitor', 'view'],
  interested: ['request-access', 'reject', 'monitor', 'view'],
  // No 'request-access' — you have asked; asking again is not a move. No
  // 'open-diligence' — the documents are shut until the founder answers. What
  // remains is giving up, which is still the investor's own call: a founder
  // sitting on a request cannot trap a deal in someone else's pipeline.
  awaiting_access: ['reject', 'monitor', 'view'],
  // No reject/mark-interested here: triage is over. The only pipeline move left
  // is concluding the DD, which happens in the workspace holding the evidence.
  diligence:  ['open-diligence', 'view'],
  invested:   ['view'],
  rejected:   ['view'],
  monitoring: ['re-engage', 'view'],
};

const SURFACES: Readonly<Record<DealStage, readonly Surface[]>> = {
  screening:  ['dealflow'],
  interested: ['dealflow'],
  // The investor's work HAS begun even though the documents are shut — they
  // have committed to diligencing this company and are waiting on an answer.
  // Hiding it from the DD page until approval would make the request vanish
  // with nowhere to watch it, so a waiting deal belongs in both places.
  awaiting_access: ['dealflow', 'diligence'],
  // Dealflow is the whole ACTIVE pipeline; the DD page is the focused
  // workspace. A deal in diligence belongs in both.
  diligence:  ['dealflow', 'diligence'],
  invested:   ['portfolio'],
  rejected:   ['archive'],
  monitoring: ['watchlist'],
};

/**
 * The stage an action moves a deal to, or null when it only navigates.
 *
 * Nothing here targets `diligence`: entry is the founder's to grant.
 */
const ACTION_TARGET: Readonly<Record<DealAction, DealStage | null>> = {
  'mark-interested': 'interested',
  'request-access': 'awaiting_access',
  reject: 'rejected',
  monitor: 'monitoring',
  're-engage': 'interested',
  'open-diligence': null,
  view: null,
};

function isKnown(stage: DealStage): boolean {
  return Object.prototype.hasOwnProperty.call(TRANSITIONS, stage);
}

export function allowedTransitions(stage: DealStage): readonly DealStage[] {
  return isKnown(stage) ? TRANSITIONS[stage] : [];
}

export function canTransition(from: DealStage, to: DealStage): boolean {
  return allowedTransitions(from).includes(to);
}

/**
 * Unknown stages offer `view` only — never a destructive action. A client older
 * than a future migration must degrade, not crash, and must not be tricked into
 * a move the server would reject.
 */
export function offeredActions(stage: DealStage): readonly DealAction[] {
  return isKnown(stage) ? ACTIONS[stage] : ['view'];
}

export function surfacesFor(stage: DealStage): readonly Surface[] {
  return isKnown(stage) ? SURFACES[stage] : ['dealflow'];
}

/**
 * Terminal is DECLARED, not derived from having no transitions. `monitoring` is
 * an ending — nothing advances out of it automatically — yet it has exactly one
 * way back (re-engage). Deriving terminality from an empty transition list
 * would classify it as live.
 */
export function isTerminal(stage: DealStage): boolean {
  return isKnown(stage) && TERMINAL.includes(stage);
}

export function actionTarget(action: DealAction): DealStage | null {
  return ACTION_TARGET[action] ?? null;
}

/** Does a card at this stage belong on this surface? */
export function appearsOn(stage: DealStage, surface: Surface): boolean {
  return surfacesFor(stage).includes(surface);
}

/**
 * Are the founder's data-room documents open to this investor?
 *
 * `diligence` is reachable ONLY through the founder's approval, and that
 * approval writes the stage and the `data_room_access` grant in one transaction
 * (`respond_to_data_room_request`). So the stage IS the answer — there is no
 * second thing to consult.
 *
 * THIS REPLACED A COLUMN. `dealflow.data_room_access` used to carry the same
 * fact, and being a second store it could disagree with the first — but worse,
 * it was a column the INVESTOR could write on their own row while a SELECT
 * policy on the founder's documents read it (measured on staging: a self-write
 * took an investor from 0 to 2 readable documents with zero founder grants).
 * Retired in 20260805160000, which also added a trigger so the stage cannot be
 * self-assigned in its place.
 *
 * NOT the same question as `appearsOn(stage, 'diligence')`, and the difference
 * is the whole reason both exist: a deal at `awaiting_access` IS on the DD
 * surface — the investor is watching their request — with the documents still
 * shut.
 *
 * `invested` IS OPEN, and that is the 2026-08-06 revoke-on-conclude decision
 * read back. `conclude_dd_report` now revokes the founder's grant for `pass`
 * and `monitor` and DELIBERATELY keeps it for `invest` — an investor who put
 * money in keeps the documents. While this returned `diligence` only, that
 * decision was invisible: the grant stayed live, `document-signed-url` would
 * still serve the files, and the one investor who actually funded the company
 * was the one shown a locked data room.
 *
 * So the stage still IS the answer, for the same reason as before — the grant
 * and the stage are written together, now on the way out as well as in. The one
 * case they part is a founder revoking by hand, which no stage records; screens
 * that hold the real grant (`useMyDataRoomGrant`) should union it in, and
 * `startup-detail.tsx` does.
 */
export function dataRoomOpen(stage: DealStage): boolean {
  return stage === 'diligence' || stage === 'invested';
}

/**
 * Is the deal waiting on the founder to answer an access request?
 *
 * This is the gap between the ask and the answer: the investor has committed to
 * diligencing this company and the founder has not yet replied. It is the reason
 * the request button must not offer a SECOND request — `awaiting_access` offers
 * no `request-access` action precisely because asking again is not a move, and a
 * button that re-asks would write a duplicate row the founder never sees as new.
 *
 * NOT the same question as `dataRoomOpen`, and the difference is why both exist.
 * `dataRoomOpen` is false here too, but for the opposite reason: there it means
 * "the documents are shut", a fact about what may be READ; here it means "we are
 * mid-conversation", a fact about what may be ASKED. A stage where the documents
 * are shut and nothing has been requested (`interested`) answers false to this
 * one and false to `dataRoomOpen` — the two only look alike from one side.
 *
 * `startup-detail.tsx` used to derive this from a `status === 'awaiting_access'`
 * literal, which drove the request button's label, disabled state and colour —
 * a behaviour decision taken from a stage literal, the exact drift the spine
 * exists to prevent.
 */
export function awaitingFounderAnswer(stage: DealStage): boolean {
  return stage === 'awaiting_access';
}

/**
 * Is the deal in the pipeline but not yet looked at?
 *
 * `screening` is where a deal ARRIVES — it is the column default, and what
 * `investor-analyze-deck` and the inbox shortlist write — so "still needs
 * triage" is the question this stage was named for. Which deals those are is a
 * property of the spine, not a dashboard's private opinion: the investor
 * dashboard used to SELECT the deals to feature with a
 * `status === 'screening'` literal, so a future untriaged stage would have
 * silently dropped out of the one list that exists to surface it.
 *
 * NOT a count. `investor-dashboard.tsx` also COUNTS deals at `screening` for
 * its pipeline tile, and that literal stays: the literal IS the question there,
 * and there is nothing to derive (ARCH-13 draws exactly this line). Here the
 * stage's MEANING is being used to decide what to show.
 */
export function awaitingTriage(stage: DealStage): boolean {
  return stage === 'screening';
}

/**
 * May a raising-feed item be created into the pipeline at this stage?
 *
 * A raising-feed item has no `dealflow` row yet, so creating one is equivalent
 * to a deal arriving at `screening` and being acted on in the same gesture —
 * the legal targets are precisely `screening`'s transitions. Written as a type
 * predicate (not a cast) so a status that passes satisfies
 * `useAddRaisingStartupToDealflow`'s narrower parameter type.
 *
 * It lived as an IDENTICAL copy in `(investor)/dealflow.tsx` and
 * `startup-detail.tsx`, each carrying a comment explaining why a route file
 * could not import from a sibling route file. Both were right about that and
 * both were solving it the wrong way round: the answer is that it belongs
 * here, with the rule it is asking about.
 *
 * `awaiting_access` is the ONE legal target of `screening` that is excluded,
 * and the exclusion is what keeps the predicate honest rather than a lie the
 * compiler believes. Reaching that stage is `request-access`, a COMPOUND write
 * whose other half is the founder's `data_room_access_requests` row;
 * `useAddRaisingStartupToDealflow` writes the dealflow row and nothing else, so
 * a feed item created straight into `awaiting_access` would sit there with
 * nobody asked and no way to re-ask. Nothing is lost by excluding it: a
 * raising-feed item CAN request access, through `useRequestDataRoomAccess`,
 * which takes `dealId: null` and writes the request row on its own.
 *
 * Without this the widening of `screening`'s transitions would have made the
 * type predicate unsound — `true` for a value outside the type it narrows to,
 * which TypeScript accepts on trust.
 */
export function isRaisingEntryStage(
  status: DealStage,
): status is Extract<DealStage, 'interested' | 'rejected' | 'monitoring'> {
  return status !== 'awaiting_access' && canTransition('screening', status);
}

/**
 * May the investor signal a commitment (soft or hard) at this stage?
 *
 * Commitment signalling is deliberately NOT a `DealAction` — the spec lists
 * commitments as a non-goal of the pipeline spine, and signalling one does not
 * move a deal between stages the way every `DealAction` does. But which stages
 * it is OFFERED at is still a property of the spine, not an opinion a screen
 * gets to hand-roll: `duediligence/[id].tsx` used to re-derive this as a
 * three-way `status === … || … || …` literal comparison, drifting out of step
 * with the spine each time a stage was added or renamed.
 *
 * `awaiting_access` is INCLUDED, deliberately. Signalling interest is a soft
 * commitment the investor makes about their own intent; it is not gated on
 * having read anything. A deal sitting here is one the investor has actively
 * asked to diligence — if anything a stronger signal than `interested` — and
 * the founder is the one holding it up.
 *
 * `diligence` stays excluded: once the documents are open, the investor is
 * expected to conclude the DD (which records a real decision and an amount),
 * not to re-signal soft interest.
 */
export function canSignalCommitment(stage: DealStage): boolean {
  return stage === 'screening' || stage === 'interested' || stage === 'awaiting_access';
}
